import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import { Newspaper, Plus, Edit, Trash2, Upload, X, Loader2 } from 'lucide-react';
import { API } from '@/App';

const BlogManagementPage = () => {
  const [posts, setPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [postForm, setPostForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    author: '',
    tags: '',
    cover_image: '',
    published: false
  });

  const coverRef = useRef();
  const [coverUploading, setCoverUploading] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=false`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    }
  };

  const resetForm = () => {
    setPostForm({
      title: '',
      content: '',
      excerpt: '',
      category: '',
      author: '',
      tags: '',
      cover_image: '',
      published: false
    });
    setEditingPost(null);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload/cms-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setPostForm(prev => ({ ...prev, cover_image: res.data.url }));
      toast.success('Image importee');
    } catch { toast.error("Erreur d'upload"); }
    setCoverUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...postForm,
        slug: postForm.title.toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, ''),
        tags: postForm.tags.split(',').map(t => t.trim()).filter(t => t),
        featured_image: postForm.cover_image || null,
        category: postForm.category || null,
      };
      delete data.cover_image;
      delete data.author;

      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, data);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/blog`, data);
        toast.success('Article créé');
      }
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId, postTitle) => {
    if (window.confirm(`Supprimer l'article "${postTitle}" ?`)) {
      try {
        await axios.delete(`${API}/blog/${postId}`);
        toast.success('Article supprimé');
        fetchPosts();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-[1800px] mx-auto" data-testid="blog-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center shadow-sm">
                <Newspaper className="text-gray-600" size={18} />
              </div>
              <div>
                <h1 className="h1-title">Gestion du Blog</h1>
                <p className="text-sm text-gray-600">Créez et gérez les articles de blog</p>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#1e3a5f] hover:from-cyan-600 hover:to-blue-700 text-white shadow-sm"
            >
              <Plus className="mr-2" size={20} />
              Nouvel article
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-6 bg-[#1e3a5f] rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-3 text-white">
            <Newspaper size={24} />
            <div>
              <p className="font-bold text-lg">Actualités & Blog</p>
              <p className="text-sm opacity-90">Partagez vos actualités avec la communauté</p>
            </div>
          </div>
        </div>

        {/* Blog Posts - Design Nouveautés */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post, index) => (
            <div 
              key={post.id} 
              className="group relative overflow-hidden rounded-2xl bg-white border-2 border-gray-100 hover:border-cyan-400 p-6 hover:shadow-xl transition-all duration-300" 
              data-testid={`post-card-${index}`}
            >
              {/* Gradient Circle Effect */}
              <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-10 group-hover:opacity-20 transition-opacity ${
                post.published ? 'bg-emerald-500' : 'bg-gray-400'
              }`}></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`rounded-full ${
                    post.published 
                      ? 'bg-green-100 text-green-700 border-green-300' 
                      : 'bg-gray-100 text-gray-700 border-gray-300'
                  }`}>
                    {post.published ? 'Publié' : 'Brouillon'}
                  </Badge>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-cyan-400 text-[#1e3a5f] hover:bg-cyan-50 rounded-full" 
                      onClick={() => {
                        setEditingPost(post);
                        setPostForm({
                          title: post.title,
                          content: post.content,
                          excerpt: post.excerpt,
                          category: post.category || '',
                          author: post.author,
                          tags: post.tags.join(', '),
                          cover_image: post.cover_image || '',
                          published: post.published
                        });
                        setDialogOpen(true);
                      }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600 border-red-400 hover:bg-red-50 rounded-full" 
                      onClick={() => handleDelete(post.id, post.title)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h3>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">{post.excerpt}</p>
                
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-medium">{post.author}</span>
                  <span>•</span>
                  <span>{new Date(post.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {post.tags.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} className="bg-cyan-100 text-cyan-700 border-cyan-300 rounded-full text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Modifier' : 'Nouvel'} article de blog</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Cover Image */}
              <div>
                <Label className="mb-1.5 block">Image de couverture</Label>
                {postForm.cover_image ? (
                  <div className="relative rounded-xl overflow-hidden h-36 bg-gray-100">
                    <img src={postForm.cover_image.startsWith('/') ? `${API.replace('/api', '')}${postForm.cover_image}` : postForm.cover_image} alt="Cover" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setPostForm({...postForm, cover_image: ''})} className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-lg hover:bg-black/80"><X size={14} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => coverRef.current?.click()} disabled={coverUploading}
                    className="w-full h-28 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#35AFE4] hover:bg-blue-50/30 transition-all" data-testid="blog-cover-upload">
                    {coverUploading ? <Loader2 size={20} className="animate-spin text-gray-400" /> : <><Upload size={20} className="text-gray-400" /><span className="text-xs text-gray-500">Importer une image</span></>}
                  </button>
                )}
                <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
              </div>
              <div>
                <Label>Titre *</Label>
                <Input
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Extrait *</Label>
                <RichTextEditor
                  value={postForm.excerpt}
                  onChange={val => setPostForm({ ...postForm, excerpt: val })}
                  placeholder="Résumé de l'article..."
                  height="80px"
                />
              </div>
              <div>
                <Label>Contenu *</Label>
                <RichTextEditor
                  value={postForm.content}
                  onChange={val => setPostForm({ ...postForm, content: val })}
                  placeholder="Contenu complet de l'article de blog..."
                  height="250px"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Auteur *</Label>
                  <Input
                    value={postForm.author}
                    onChange={(e) => setPostForm({ ...postForm, author: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Input
                    value={postForm.category}
                    onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
                    placeholder="Actualité, Congrès, Formation..."
                    data-testid="blog-category-input"
                  />
                </div>
              </div>
              <div>
                <Label>Tags (separes par des virgules)</Label>
                <Input
                  value={postForm.tags}
                  onChange={(e) => setPostForm({ ...postForm, tags: e.target.value })}
                  placeholder="contactologie, lentilles, sante"
                />
              </div>
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Switch
                  checked={postForm.published}
                  onCheckedChange={(checked) => setPostForm({ ...postForm, published: checked })}
                />
                <Label>Publier l'article</Label>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
                {loading ? 'Sauvegarde...' : editingPost ? 'Mettre à jour' : 'Créer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BlogManagementPage;
